import React from 'react'
import {Modal, Pressable, Text, TextInput, View} from 'react-native'

import {EVM_NETWORKS} from '#/config/evm'
import {isMetaMaskAvailable, openPortfolio, sendTransaction} from '../metamaskBridge'
import {ReceiveModal} from './ReceiveModal'
import {SendModal} from './SendModal'

type AssetOption = {
  id: string
  symbol: string
  name: string
}

type Props = {
  assets: AssetOption[]
  address?: string
  chainId?: string
  isDesktop: boolean
}

const TABS = [
  {key: 'buy', label: 'Acheter'},
  {key: 'sell', label: 'Vendre'},
  {key: 'swap', label: 'Convertir'},
] as const

export function TradePanel({assets, address, chainId, isDesktop}: Props) {
  const [tab, setTab] = React.useState<(typeof TABS)[number]['key']>('buy')
  const [amount, setAmount] = React.useState('')
  const [selectedAsset, setSelectedAsset] = React.useState<AssetOption | null>(
    assets[0] || null,
  )
  const [showAssetPicker, setShowAssetPicker] = React.useState(false)
  const [showReceive, setShowReceive] = React.useState(false)
  const [showSend, setShowSend] = React.useState(false)
  const [sendError, setSendError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!selectedAsset && assets.length) {
      setSelectedAsset(assets[0])
    }
  }, [assets, selectedAsset])

  const onContinue = () => {
    openPortfolio(tab)
  }

  const onSend = async (to: string, valueEth: string) => {
    setSendError(null)
    if (!to) {
      setSendError('Adresse requise.')
      return
    }
    try {
      await sendTransaction({to, valueEth})
      setShowSend(false)
    } catch (err: any) {
      setSendError(err?.message || 'Envoi impossible.')
    }
  }

  const networkLabel = (() => {
    if (!chainId) return '—'
    const numeric = chainId.startsWith('0x')
      ? Number.parseInt(chainId, 16)
      : Number(chainId)
    const match = Object.values(EVM_NETWORKS).find(cfg => cfg.id === numeric)
    if (match) return match.name
    return Number.isFinite(numeric) ? `Chain ${numeric}` : '—'
  })()

  return (
    <View
      className={`rounded-2xl border border-black/10 bg-white/80 p-5 shadow-sm dark:border-white/10 dark:bg-neutral-900/70 ${
        isDesktop ? '' : 'mt-6'
      }`}>
      <View className="flex-row gap-2 rounded-full bg-black/5 p-1 dark:bg-white/5">
        {TABS.map(item => (
          <Pressable
            accessibilityRole="button"
            key={item.key}
            onPress={() => setTab(item.key)}
            className={`flex-1 rounded-full px-3 py-2 ${
              tab === item.key
                ? 'bg-neutral-900 dark:bg-white'
                : 'bg-transparent'
            }`}>
            <Text
              className={`text-center text-xs font-semibold ${
                tab === item.key
                  ? 'text-white dark:text-neutral-900'
                  : 'text-neutral-600 dark:text-neutral-300'
              }`}>
              {item.label}
            </Text>
          </Pressable>
        ))}
      </View>

      <View className="mt-4 gap-3">
        <Text className="text-xs text-neutral-500 dark:text-neutral-400">Montant</Text>
        <TextInput
          accessibilityLabel="Text input field"
          value={amount}
          onChangeText={setAmount}
          placeholder="0.0"
          keyboardType="decimal-pad"
          placeholderTextColor="#9ca3af"
          className="rounded-xl border border-black/10 bg-white/90 px-3 py-2.5 text-sm text-neutral-900 dark:border-white/10 dark:bg-neutral-900/60 dark:text-white"
        />
        <Text className="text-xs text-neutral-500 dark:text-neutral-400">Asset</Text>
        <Pressable
          accessibilityRole="button"
          onPress={() => setShowAssetPicker(true)}
          className="rounded-xl border border-black/10 bg-white/90 px-3 py-2.5 active:opacity-90 dark:border-white/10 dark:bg-neutral-900/60">
          <Text className="text-sm text-neutral-900 dark:text-white">
            {selectedAsset ? `${selectedAsset.symbol} · ${selectedAsset.name}` : 'Choisir'}
          </Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          onPress={onContinue}
          className="mt-2 rounded-full bg-neutral-900 px-4 py-2.5 active:opacity-90 dark:bg-white">
          <Text className="text-center text-xs font-semibold text-white dark:text-neutral-900">
            Continuer / Verifier l'ordre
          </Text>
        </Pressable>
      </View>

      <View className="mt-6 gap-2">
        <Text className="text-xs text-neutral-500 dark:text-neutral-400">Actions rapides</Text>
        <View className="flex-row flex-wrap gap-2">
          <Pressable
            accessibilityRole="button"
            onPress={() => {
              if (!isMetaMaskAvailable()) {
                setSendError('MetaMask requis pour envoyer.')
                setShowSend(true)
                return
              }
              setShowSend(true)
            }}
            className="rounded-full border border-black/10 px-3 py-2 active:opacity-90 dark:border-white/15">
            <Text className="text-xs text-neutral-700 dark:text-neutral-200">Envoyer</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            onPress={() => setShowReceive(true)}
            className="rounded-full border border-black/10 px-3 py-2 active:opacity-90 dark:border-white/15">
            <Text className="text-xs text-neutral-700 dark:text-neutral-200">Recevoir</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            onPress={() => openPortfolio('home')}
            className="rounded-full border border-black/10 px-3 py-2 active:opacity-90 dark:border-white/15">
            <Text className="text-xs text-neutral-700 dark:text-neutral-200">Deposer</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            onPress={() => openPortfolio('home')}
            className="rounded-full border border-black/10 px-3 py-2 active:opacity-90 dark:border-white/15">
            <Text className="text-xs text-neutral-700 dark:text-neutral-200">Retirer</Text>
          </Pressable>
        </View>
      </View>

      <Modal
        transparent
        animationType="fade"
        visible={showAssetPicker}
        onRequestClose={() => setShowAssetPicker(false)}>
        <Pressable
          accessibilityRole="button"
          onPress={() => setShowAssetPicker(false)}
          className="flex-1 items-center justify-center bg-black/60 px-4">
          <Pressable
            accessibilityRole="button"
            onPress={event => event.stopPropagation?.()}
            className="w-full max-w-md rounded-2xl border border-black/10 bg-white/95 p-5 shadow-lg dark:border-white/10 dark:bg-neutral-900/90">
            <Text className="text-sm font-semibold text-neutral-900 dark:text-white">
              Choisir un asset
            </Text>
            <View className="mt-4 gap-2">
              {assets.map(asset => (
                <Pressable
                  accessibilityRole="button"
                  key={asset.id}
                  onPress={() => {
                    setSelectedAsset(asset)
                    setShowAssetPicker(false)
                  }}
                  className="rounded-xl border border-black/10 px-3 py-2 active:opacity-90 dark:border-white/10">
                  <Text className="text-sm text-neutral-800 dark:text-neutral-200">
                    {asset.symbol} · {asset.name}
                  </Text>
                </Pressable>
              ))}
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <ReceiveModal
        open={showReceive}
        onClose={() => setShowReceive(false)}
        address={address}
        networkLabel={networkLabel}
      />
      <SendModal
        open={showSend}
        onClose={() => {
          setShowSend(false)
          setSendError(null)
        }}
        onSend={onSend}
        error={sendError}
      />
    </View>
  )
}
